<script setup lang="ts">
import { hexAlpha, mixColor, type LevelTier } from '@/utils/levels';
import type { TierState } from '@/composables/useLevelsProgress';

const props = defineProps<{
  /** A fila do swiper: degrau "Sem elo" na frente + os 14 elos. */
  steps: LevelTier[];
  viewIndex: number;
  viewTier: LevelTier;
  state: TierState;
  playerLevelLabel: string;
  /** Faixa de XP do elo em foco, já formatada — alimenta o dropdown de info. */
  xpRange: string;
}>();

/**
 * Dropdown de info: o pitch + fatos do elo (LevelsTierFacts) saíram do fluxo
 * da página e moram atrás do ⓘ ao lado do título. Trocar de elo fecha.
 * O "Sem elo" não ganha o ícone: não há fatos de elo pra contar ali.
 */
const isInfoOpen = ref(false);

watch(
  () => props.viewTier.key,
  () => {
    isInfoOpen.value = false;
  },
);

/**
 * A barriguinha do painel aponta pro ⓘ, mas o ⓘ anda conforme a largura do
 * nome do elo ("Nível Ouro" x "Nível Ametista"). Medimos a posição real do
 * botão quando o painel abre — o painel fecha a cada troca de elo, então a
 * medida nunca fica velha.
 */
const rootEl = ref<HTMLElement | null>(null);
const infoButtonEl = ref<HTMLElement | null>(null);
const caretLeft = ref(0);

watch(isInfoOpen, async (open) => {
  if (!open) return;

  await nextTick();

  const root = rootEl.value?.getBoundingClientRect();
  const button = infoButtonEl.value?.getBoundingClientRect();

  if (!root || !button) return;

  caretLeft.value = button.left + button.width / 2 - root.left;
});

/** Clique fora do painel (e fora do ⓘ, que já alterna sozinho) fecha. */
const panelEl = ref<HTMLElement | null>(null);

function onDocumentPointerDown(event: PointerEvent) {
  if (!isInfoOpen.value) return;

  const target = event.target as Node;

  if (panelEl.value?.contains(target) || infoButtonEl.value?.contains(target)) {
    return;
  }

  isInfoOpen.value = false;
}

onMounted(() =>
  document.addEventListener('pointerdown', onDocumentPointerDown),
);
onUnmounted(() =>
  document.removeEventListener('pointerdown', onDocumentPointerDown),
);

const emit = defineEmits<{
  step: [direction: number];
  select: [index: number];
}>();

/**
 * Monta um pilar a partir da posição dele na fila. `index` é posicional, não
 * necessariamente o índice do elo: os dois pilares repetidos das pontas ocupam
 * -1 e LEVEL_TIER_COUNT (ver `pillars`).
 */
function makePillar(tier: LevelTier, index: number, isGhost: boolean) {
  const offset = index - props.viewIndex;
  const isNear = Math.abs(offset) <= 1;
  const isCenter = offset === 0;
  const width = isCenter ? 268 : 228;

  return {
    tier,
    index,
    isNear,
    isCenter,
    // O repetido nunca é clicável nem focável: é só enfeite de continuidade.
    isInteractive: isNear && !isGhost,
    // O repetido duplica um elo que já existe na fila, então a chave dele não
    // pode ser a do elo — senão o `v-for` vê duas chaves iguais.
    key: isGhost ? `ghost:${index}:${tier.key}` : tier.key,
    style: {
      width: `${width}px`,
      height: `${width}px`,
      marginLeft: `${-width / 2}px`,
      left: `calc(50% + ${offset * 112}px)`,
      bottom: isCenter ? '14px' : '0px',
      zIndex: isCenter ? 3 : 1,
      // O repetido entra mais apagado que um vizinho de verdade: assim a fila
      // parece continuar sem convidar o clique que ele não aceita.
      opacity: isCenter ? 1 : isNear ? (isGhost ? 0.38 : 0.85) : 0,
      filter: isCenter
        ? `drop-shadow(0 16px 38px ${hexAlpha(tier.color, 0.55)})`
        : 'saturate(.82) brightness(.7) drop-shadow(0 12px 26px rgba(0,0,0,.5))',
    },
    haloStyle: {
      // Altura explicita junto da largura: `aspect-square` nao gera CSS neste
      // projeto (o plugin @tailwindcss/aspect-ratio mata as classes nomeadas
      // do core), e sem altura o halo colapsava.
      width: isCenter ? '184px' : '148px',
      height: isCenter ? '184px' : '148px',
      // Centrado por porcentagem, não por px. As artes têm larguras
      // diferentes (331 a 384px de canvas para a mesma altura), então a arte
      // renderizada ocupa de 165 a 192px dentro da caixa; um `left` fixo em
      // px deixava o brilho fora do emblema em quase todos os elos.
      left: '50%',
      transform: 'translateX(-50%)',
      top: isCenter ? '2px' : '12px',
      background: `radial-gradient(${hexAlpha(tier.color, isCenter ? 0.42 : 0.22)},transparent 68%)`,
    },
  };
}

/**
 * Só três pilares aparecem: o elo em foco no centro e um vizinho de cada lado.
 * O resto fica montado mas com opacidade zero, o que mantém a fila estável
 * quando a navegação anda (nada entra ou sai do DOM) — é isso que deixa a
 * troca deslizar em vez de piscar: cada pilar transiciona da posição velha
 * pra nova.
 *
 * As duas pontas ganham um pilar repetido — o último elo antes do primeiro e o
 * primeiro depois do último — só pra fila nunca parecer cortada. Não é loop:
 * eles não recebem clique nem foco, e a navegação continua parando em 0 e em
 * LEVEL_TIER_COUNT - 1. Como ficam em -1 e LEVEL_TIER_COUNT, cada um só sai da
 * opacidade 0 quando o foco está na ponta correspondente.
 */
const pillars = computed(() => {
  const steps = props.steps;

  return [
    makePillar(steps[steps.length - 1], -1, true),
    ...steps.map((step, index) => makePillar(step, index, false)),
    makePillar(steps[0], steps.length, true),
  ];
});

/** "Sem elo" não ganha o prefixo "Nível" — não é um elo. */
const title = computed(() => {
  if (props.viewTier.isUnranked) return props.viewTier.name;

  return props.viewTier.isVip
    ? props.viewTier.name
    : `Nível ${props.viewTier.name}`;
});

const kicker = computed(() => {
  // O degrau "Sem elo" não é elo pra ser concluído nem bloqueado: ou é onde
  // você está, ou é só o ponto de partida da régua.
  if (props.viewTier.isUnranked) {
    return props.state === 'current' ? 'VOCÊ ESTÁ AQUI' : 'PONTO DE PARTIDA';
  }

  if (props.state === 'current') return 'SEU ELO ATUAL';

  return props.state === 'done' ? 'ELO CONCLUÍDO' : 'ELO BLOQUEADO';
});

const hasPrev = computed(() => props.viewIndex > 0);
const hasNext = computed(() => props.viewIndex < props.steps.length - 1);
</script>

<template>
  <!--
    Coluna do pódio: fica ao lado do cartão de XP, como no protótipo desktop —
    título com as setas em cima e o palco de pilares embaixo.
  -->
  <div
    ref="rootEl"
    class="relative flex flex-col gap-2"
  >
    <!-- Brilho da coluna, atrás de tudo -->
    <span
      aria-hidden="true"
      class="levels-column-glow pointer-events-none absolute left-1/2 top-[38%] h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[20px]"
      :style="{
        background: `radial-gradient(${hexAlpha(viewTier.color, 0.16)},transparent 62%)`,
      }"
    ></span>

    <!--
      Título + setas. A fileira é centrada como no protótipo, mas o título tem
      largura fixa: com a largura livre as setas andavam pra dentro e pra fora a
      cada troca de elo ("Nível Bronze" x "Nível Diamante" x "VIP 3").
    -->
    <div class="relative flex items-center justify-center gap-4">
      <button
        aria-label="Elo anterior"
        class="flex h-8 w-8 flex-none items-center justify-center rounded-full text-white/[0.65] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
        :class="hasPrev ? 'opacity-100' : 'opacity-30'"
        :disabled="!hasPrev"
        type="button"
        @click="emit('step', -1)"
      >
        <LevelsNavArrow
          class="h-4 w-4"
          direction="prev"
        />
      </button>

      <div
        :key="viewTier.key"
        class="levels-title flex w-[264px] flex-none flex-col items-center gap-1"
      >
        <span
          class="text-[11px] font-bold uppercase leading-[15px] tracking-[0.18em]"
          :style="{
            color:
              state === 'current'
                ? mixColor(viewTier.color, '#ffffff', 0.55)
                : state === 'done'
                  ? mixColor(viewTier.accent, '#ffffff', 0.45)
                  : 'rgba(255,255,255,.42)',
          }"
        >
          {{ kicker }}
        </span>
        <span class="relative flex items-center justify-center gap-2">
          <span
            class="whitespace-nowrap text-center text-[30px] font-extrabold leading-9 tracking-[-0.04em] text-white"
          >
            {{ title }}
          </span>
          <!-- Info ao lado do nome do elo: abre o painel de pitch + fatos, que
               saiu do fluxo da página. O "Sem elo" não tem fatos → sem ícone. -->
          <!-- Ícone pelado, sem círculo: o info.svg já traz o disco próprio.
               No "Sem elo" também existe — o painel sai só com o texto, sem
               os quadrinhos (o TierFacts esconde os fatos do degrau 0). -->
          <button
            ref="infoButtonEl"
            :aria-expanded="isInfoOpen"
            aria-label="Detalhes do elo"
            class="flex flex-none items-center justify-center border-none bg-transparent p-0"
            :style="{
              color: mixColor(
                viewTier.color,
                '#ffffff',
                isInfoOpen ? 0.72 : 0.45,
              ),
            }"
            type="button"
            @click="isInfoOpen = !isInfoOpen"
          >
            <NuxtIcon
              class="text-[18px]"
              name="info"
            />
          </button>
        </span>
        <!--
          O chip do nível só vale no elo atual, mas o espaço dele é reservado
          sempre (`invisible`, não `v-if`): senão o bloco encurta ao navegar e o
          palco pula na tela.
        -->
        <span
          :aria-hidden="state === 'current' ? undefined : 'true'"
          class="rounded px-2 py-0.5 text-[10px] font-bold uppercase leading-[14px] tracking-[0.1em]"
          :class="state === 'current' ? '' : 'invisible'"
          :style="{
            background: 'rgba(255,255,255,.92)',
            color: mixColor(viewTier.color, '#050A18', 0.45),
          }"
        >
          {{ playerLevelLabel }}
        </span>
      </div>

      <button
        aria-label="Próximo elo"
        class="flex h-8 w-8 flex-none items-center justify-center rounded-full text-white/[0.65] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
        :class="hasNext ? 'opacity-100' : 'opacity-30'"
        :disabled="!hasNext"
        type="button"
        @click="emit('step', 1)"
      >
        <LevelsNavArrow
          class="h-4 w-4"
          direction="next"
        />
      </button>
    </div>

    <!--
      Painel do info: pitch + fatos do elo flutuando sobre o palco, ancorado
      logo abaixo do título. Fica FORA do `.levels-stage` de propósito — lá
      dentro o overflow/máscara cortaria o painel.
    -->
    <template v-if="isInfoOpen">
      <!-- Barriguinha: emenda o ⓘ (posição medida) na borda do painel -->
      <!-- topo colado no ⓘ: o botão termina em ~46px do topo da coluna -->
      <span
        aria-hidden="true"
        class="levels-info-caret absolute top-[68px] z-[6] h-2.5 w-2.5 -translate-x-1/2 rotate-45"
        :style="{
          left: `${caretLeft}px`,
          background: 'rgba(4,9,24,.96)',
          boxShadow: `inset 1.5px 1.5px 0 0 ${hexAlpha(viewTier.color, 0.35)}`,
        }"
      ></span>
      <!-- `w-max`: o painel cresce até os 3 cards caberem com texto completo
           numa linha — pode ficar mais largo que a coluna do pódio, é popover. -->
      <div
        ref="panelEl"
        class="levels-info-panel absolute left-1/2 top-[73px] z-[5] w-max max-w-[calc(100vw-48px)] -translate-x-1/2 rounded-2xl p-4"
        :style="{
          background: 'rgba(4,9,24,.96)',
          boxShadow: `inset 0 0 0 1px ${hexAlpha(viewTier.color, 0.35)},0 24px 54px -24px rgba(0,0,0,.9)`,
        }"
      >
        <LevelsTierFacts
          compact
          :tier="viewTier"
          :xp-range="xpRange"
        />
      </div>
    </template>

    <!--
      Palco. 310px de altura dão folga pro pilar do centro (268px levantado
      14px) caber inteiro dentro do corte, e a máscara vertical apaga topo e
      base — é o que faz a fila parecer flutuando, como no protótipo.
    -->
    <div class="levels-stage relative -mt-[34px] h-[310px] w-full">
      <!-- Chão de luz sob a fila -->
      <span
        aria-hidden="true"
        class="pointer-events-none absolute -bottom-3.5 left-1/2 -ml-[300px] h-[130px] w-[600px] rounded-[50%] blur-[30px]"
        :style="{
          background: `radial-gradient(closest-side,${hexAlpha(viewTier.color, 0.3)},transparent 74%)`,
        }"
      ></span>
      <!--
        Os pilares distantes ficam montados com opacidade 0 pra fila não tremer
        quando a navegação anda; nesse estado saem da árvore de acessibilidade e
        do foco. Os visíveis são botões de verdade, com nome — antes levavam
        `aria-hidden` junto de `tabindex="0"`, ou seja, alcançáveis por teclado
        mas invisíveis pro leitor de tela. Os repetidos das pontas seguem a
        mesma regra dos distantes: visíveis, mas fora do clique e do foco.
      -->
      <button
        v-for="pillar in pillars"
        :key="pillar.key"
        :aria-hidden="pillar.isInteractive ? undefined : 'true'"
        :aria-label="`Ver o elo ${pillar.tier.name}`"
        class="transition levels-pillar absolute border-none bg-transparent p-0"
        :class="pillar.isInteractive ? '' : 'pointer-events-none'"
        :style="pillar.style"
        :tabindex="pillar.isInteractive ? 0 : -1"
        type="button"
        @click="pillar.isInteractive && emit('select', pillar.index)"
      >
        <span
          aria-hidden="true"
          class="levels-halo transition absolute rounded-full blur-[18px]"
          :style="pillar.haloStyle"
        ></span>
        <span
          class="relative block h-full w-full bg-contain bg-bottom bg-no-repeat"
          :style="{ backgroundImage: `url(${pillar.tier.pillar})` }"
        ></span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* Entrada do painel de info — keyframe, não transition (reset global). */
@keyframes levelsInfoIn {
  0% {
    opacity: 0;
    transform: translate(-50%, -6px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.levels-info-panel {
  animation: levelsInfoIn 0.28s cubic-bezier(0.2, 0.75, 0.3, 1) both;
}

/* A barriguinha só esmaece: animar transform aqui atropelaria o rotate-45. */
@keyframes levelsInfoCaretIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.levels-info-caret {
  animation: levelsInfoCaretIn 0.28s ease both;
}

/*
  O deslize do swiper de elos. A dança de classes aqui é obrigatória:

  1. o main.scss zera `transition` no app inteiro com !important, e a única
     porta de saída é a classe `transition` (`*:not(.transition)`) — por isso
     os pilares carregam ela;
  2. só que `transition` também é utility do Tailwind, e o tailwind.config roda
     com `important: true` — a utility impõe a PRÓPRIA lista de propriedades
     (color/opacity/transform/filter, 150ms) com !important, que não tem
     left/width/bottom: o pilar teleportava e só a opacidade esmaecia — o
     "fantasma" atrás do pilar.

  A regra abaixo vence a utility no critério de desempate de !important
  (especificidade: 2 classes + escopo contra 1 classe) e devolve o deslize do
  protótipo. Inline não resolveria: declaração inline sem !important perde
  para regra !important de folha.
*/
.levels-pillar.transition {
  transition:
    left 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    width 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    height 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    bottom 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    margin-left 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    opacity 0.45s ease,
    filter 0.45s ease !important;
}

/* O halo cresce junto com o pilar; sem isso ele estala no meio do deslize. */
.levels-halo.transition {
  transition:
    width 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    height 0.55s cubic-bezier(0.3, 0.9, 0.25, 1),
    top 0.55s cubic-bezier(0.3, 0.9, 0.25, 1) !important;
}

/* Corte + esfumaçado do palco, iguais aos do protótipo: o topo entra suave e a
   base dos pilares dissolve, o que dá a leitura de fila flutuando. Fica no CSS
   (e não em classe utilitária) porque `mask-image` precisa do par
   `-webkit-`. */
.levels-stage {
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.45) 5%,
    #000 13%,
    #000 40%,
    rgba(0, 0, 0, 0.82) 54%,
    rgba(0, 0, 0, 0.5) 68%,
    rgba(0, 0, 0, 0.18) 82%,
    transparent 94%
  );
  mask-image: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.45) 5%,
    #000 13%,
    #000 40%,
    rgba(0, 0, 0, 0.82) 54%,
    rgba(0, 0, 0, 0.5) 68%,
    rgba(0, 0, 0, 0.18) 82%,
    transparent 94%
  );
}

@keyframes levelsColumnGlow {
  0%,
  100% {
    opacity: 0.72;
  }
  50% {
    opacity: 1;
  }
}

.levels-column-glow {
  animation: levelsColumnGlow 4.6s ease-in-out infinite;
}

@keyframes levelsHaloPulse {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.9;
  }
}

.levels-halo {
  animation: levelsHaloPulse 4.2s ease-in-out infinite;
}

/* `:key` no bloco do título remonta o nó a cada troca de elo, reiniciando a
   animação de entrada — `transition` é zerada globalmente no main.scss. */
@keyframes levelsTitleIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.levels-title {
  animation: levelsTitleIn 0.45s cubic-bezier(0.2, 0.75, 0.3, 1) both;
}
</style>
